import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Form, Input, Select } from 'antd'
import { createSchemaFieldRule } from 'antd-zod'
import type { z } from 'zod'
import { botCreateSchema } from '@super-mega/server/contracts/bot'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'

const fieldRule = createSchemaFieldRule(botCreateSchema)
type Values = z.infer<typeof botCreateSchema>

export function BotCreateModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const access = useAccess()
  const { message } = App.useApp()
  const create = useMutation(
    trpc.bot.create.mutationOptions({
      onSuccess: () => cache.invalidateQueries(trpc.bot.list.queryFilter())
    })
  )
  const canCreate = access.all(['bot.create', 'bot.credential.update'])

  return (
    <ModalForm<Values>
      title="新增机器人"
      width="44rem"
      open={open}
      initialValues={{ kind: 'customer' }}
      layout="vertical"
      disabled={create.isPending || !canCreate}
      scrollToFirstError={{ focus: true }}
      modalProps={{
        destroyOnHidden: true,
        mask: { closable: false },
        closable: !create.isPending,
        afterClose: () => create.reset()
      }}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !create.isPending) onClose()
      }}
      onFinish={async (values) => {
        if (!canCreate) return false
        try {
          await create.mutateAsync(botCreateSchema.parse(values))
          void message.success('机器人已创建，请进入详情的“MCP 接入”生成接入密钥')
          return true
        } catch {
          return false
        }
      }}
      submitter={{
        searchConfig: { submitText: '创建机器人' },
        submitButtonProps: { loading: create.isPending, disabled: !canCreate },
        resetButtonProps: { disabled: create.isPending }
      }}
    >
      {create.isError && <Alert type="error" title={create.error.message} showIcon className="mb-4" />}
      <section aria-labelledby="bot-basic-title">
        <h3 id="bot-basic-title" className="mb-3 text-sm font-semibold">
          基本信息
        </h3>
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <Form.Item name="name" label="机器人名称" rules={[fieldRule]} required>
            <Input placeholder="例如：客户服务助手" maxLength={100} />
          </Form.Item>
          <Form.Item name="kind" label="用途" rules={[fieldRule]} required>
            <Select
              options={[
                { value: 'customer', label: '客户群客服' },
                { value: 'internal', label: '内部助手' }
              ]}
            />
          </Form.Item>
        </div>
        <Form.Item name="remark" label="备注" rules={[fieldRule]}>
          <Input.TextArea placeholder="填写用途说明或维护备注" maxLength={1000} showCount rows={3} />
        </Form.Item>
        <p className="text-content-muted mb-4 text-xs">客户客服支持内部群和外部群；内部助手仅供内部群使用。</p>
      </section>
      <section aria-labelledby="bot-wecom-title">
        <h3 id="bot-wecom-title" className="mb-1 text-sm font-semibold">
          企微连接
        </h3>
        <p className="text-content-muted mb-3 text-sm">
          填写企业微信提供的机器人 ID 和 Secret，用于 Hermes 连接企微收发消息。
        </p>
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <Form.Item name="wecomBotId" label="企微机器人 ID" rules={[fieldRule]} required>
            <Input placeholder="请输入机器人 ID" autoComplete="off" maxLength={255} />
          </Form.Item>
          <Form.Item name="secret" label="企微机器人密钥（Secret）" rules={[fieldRule]} required>
            <Input.Password placeholder="请输入机器人密钥" autoComplete="new-password" maxLength={4096} />
          </Form.Item>
        </div>
      </section>
      <p className="text-content-muted mt-4 mb-0 text-sm">
        创建后默认停用。请进入机器人详情配置业务权限，在“MCP 接入”中生成 Hermes 接入密钥，再启用使用。
      </p>
    </ModalForm>
  )
}
